import React, { useRef, useState } from 'react'
import { apiJson, ApiError } from '../../lib/api'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { useLayer } from '../../components/layers'
import { PendingButton, Failed } from '../../components/pending'
import { plural } from '../wages/format'
import { workbook, XLSX_TYPE } from '../batches/xlsx'
import { MAX_FILE_BYTES, toBase64 } from '../sites/ImportDialog'
import o from '../../components/Overlay.module.css'
import pv from './Overlays.module.css'
import s from '../sites/ImportDialog.module.css'

// The manual weight import (h 2522, 2652; lifted from the Site import, h
// 2771-2773): a file of date, item code and gross kg lines sets each named
// cycle's gross weight as a manual correction would. Nothing here judges a
// line. The server reads the file on upload (the preview) and again at
// import, and the import sends back the counts it was shown, so a cycle or a
// day that changed in between is refused with the new preview rather than
// imported on the old one. A line that cannot be applied (a locked day, no
// cycle or several of that item on that date, a weighed bin) is held back;
// the others apply. Each change is undone on its cycle, like any correction.

export interface WeightCounts {
  lines: number
  updates: number
  unchanged: number
  held: number
}

export interface WeightLine {
  row: number
  date: string
  item_code: string
  gross_kg: number | string
  status: 'update' | 'unchanged' | 'held'
  reason?: string
  cycle_id?: string
  worker_name?: string | null
  from_kg?: number | null
}

export interface WeightPreview {
  sheet: string
  ignored: string[]
  counts: WeightCounts
  lines: WeightLine[]
}

const SHOWN = 200
const kg = (v: number | string | null | undefined) => (typeof v === 'number' ? `${v.toFixed(2).replace(/\.?0+$/, '')} kg` : v ? String(v) : '—')
const err = (e: any, fallback: string) => (e?.message as string) || fallback

export const TEMPLATE_NAME = 'weight-import.xlsx'

// The template: a Weights sheet with its three headings and one example line
// to overwrite, and an Instructions sheet saying what each column takes.
export function weightTemplate(): Uint8Array<ArrayBuffer> {
  return workbook([
    { name: 'Weights', rows: [['Date', 'Item code', 'Gross kg']], widths: [12, 14, 10] },
    {
      name: 'Instructions',
      widths: [100],
      rows: [
        ['Fill in one line per cycle on the Weights sheet, then upload the workbook in Product activity › Import weights.'],
        ['Date: the cycle’s work date, as YYYY-MM-DD or an Excel date. A date like 3/11/2026 is held back, since it reads differently by country.'],
        ['Item code: the tag’s item code, as on the cycle.'],
        ['Gross kg: the gross weight in kilograms, more than 0 and under 100,000. It replaces the cycle’s gross weight.'],
        ['A line changes one cycle or nothing. It is held back, with the reason, when no cycle or more than one of that item code is on that date, the day is locked, or the cycle is in a weighed bin.'],
        ['Each change is recorded on its cycle and undone there. Other columns are not read, so an edited Product activity export can be uploaded as it is.'],
      ],
      protect: true,
    },
  ])
}

async function fileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer())
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer))
    r.onerror = () => reject(r.error)
    r.readAsArrayBuffer(file)
  })
}

export function WeightImportDialog({ user, onClose, onDone }: { user: any; onClose: () => void; onDone: (message: string) => void }) {
  const [file, setFile] = useState<{ name: string; data: string } | null>(null)
  const [preview, setPreview] = useState<WeightPreview | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const { startTask } = useTasks()
  // A layer (h 425-458): a file read but not imported is unsaved, so closing
  // then asks first.
  const dialogEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: !!file && !busy, el: dialogEl, modal: true, busy })

  const template = () => startTask(fileTask('Weight import template', 'Date, item code and gross kg', TEMPLATE_NAME, XLSX_TYPE, weightTemplate))

  const choose = async (f: File | undefined) => {
    setPreview(null)
    setFile(null)
    setError('')
    if (!f) return
    if (f.size > MAX_FILE_BYTES) {
      setError(`This file is larger than ${MAX_FILE_BYTES / 1024} KB. Import the weights in parts.`)
      return
    }
    setBusy(true)
    try {
      const picked = { name: f.name, data: toBase64(await fileBytes(f)) }
      const r = await apiJson<{ preview: WeightPreview }>(user, '/api/product-activity/weights/import', { method: 'POST', body: { file_name: picked.name, data: picked.data } })
      setFile(picked)
      setPreview(r.preview)
    } catch (e: any) {
      setError(err(e, 'The file could not be read'))
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (busy || !file || !preview) return
    const c = preview.counts
    setBusy(true)
    setError('')
    try {
      const r = await apiJson<{ message: string; closed_dates: string[] }>(user, '/api/product-activity/weights/import', {
        method: 'POST',
        body: {
          file_name: file.name,
          data: file.data,
          commit: true,
          expect: { updates: c.updates, unchanged: c.unchanged, held: c.held },
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      })
      const closed = r.closed_dates.length ? ` · ${r.closed_dates.join(', ')} ${r.closed_dates.length === 1 ? 'is' : 'are'} closed: recorded in each cycle’s adjustments` : ''
      onDone(`Weight import: ${r.message}${closed}`)
    } catch (e: any) {
      // Counts that moved since the preview come back with the preview as it
      // now reads.
      if (e instanceof ApiError && e.body?.preview) setPreview(e.body.preview)
      setError(err(e, 'Nothing was imported'))
    } finally {
      setBusy(false)
    }
  }

  const c = preview?.counts
  const blocked = !preview ? (busy ? '' : 'Choose a file first') : !c!.updates ? 'Nothing in this file changes a weight' : ''

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={dialogEl} tabIndex={-1} className={`${o.modal} ${o.wide}`} role="dialog" aria-label="Import weights">
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Import weights</h2>
            <p className={o.sub}>
              Sets the gross weight of the named cycles from a file: one line per cycle, with its date, item code and gross kg. Each change is recorded on its cycle as a correction and undone there. A line that cannot be applied is held back with the reason; the others still apply.
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
        <div className={s.steps}>
          <button type="button" className={o.ghost} onClick={template}>
            Download the template
          </button>
          <label className={o.fieldLabel} htmlFor="wimp-file">
            Upload the file (.xlsx or .csv)
          </label>
          <input
            id="wimp-file"
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(e) => choose(e.target.files?.[0])}
          />
          {busy && !preview && <span className={s.file}>Reading…</span>}
        </div>

        {preview && <PreviewBody preview={preview} fileName={file?.name || ''} />}

        {preview && c!.updates > 0 && (
          <>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="wimp-reason">
                Reason (optional)
              </label>
              <input id="wimp-reason" className={o.input} value={reason} maxLength={500} placeholder="Weight import" onChange={(e) => setReason(e.target.value)} disabled={busy} />
            </div>
          </>
        )}
        </div>

        <div className={o.foot}>
          {blocked && <span className={o.footNote}>{blocked}</span>}
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <PendingButton className={o.primary} busy={busy} pending="Importing…" disabled={!!blocked || !preview} onClick={submit}>
            {c && c.updates ? `Set ${plural(c.updates, 'weight', 'weights')}` : 'Import'}
          </PendingButton>
          <Failed text={error} />
        </div>
      </div>
      {confirm}
    </div>
  )
}

function PreviewBody({ preview, fileName }: { preview: WeightPreview; fileName: string }) {
  const { counts: c } = preview
  const updates = preview.lines.filter((l) => l.status === 'update')
  const unchanged = preview.lines.filter((l) => l.status === 'unchanged')
  const held = preview.lines.filter((l) => l.status === 'held')
  return (
    <>
      <div className={s.file}>
        {fileName}: {plural(c.lines, 'line', 'lines')} on the {preview.sheet} sheet
      </div>
      <div className={s.counts} data-testid="weight-import-counts">
        <div className={s.count}>
          <span className={s.countHead}>Will be set</span>
          <span className={s.countMain}>{plural(c.updates, 'weight', 'weights')}</span>
          <span className={s.countNote}>Each recorded on its cycle</span>
        </div>
        <div className={s.count}>
          <span className={s.countHead}>Already this weight</span>
          <span className={s.countMain}>{plural(c.unchanged, 'line', 'lines')}</span>
          <span className={s.countNote}>Nothing to change</span>
        </div>
        <div className={`${s.count} ${c.held ? s.countHeld : ''}`}>
          <span className={s.countHead}>Held back</span>
          <span className={s.countMain}>{plural(c.held, 'line', 'lines')}</span>
          <span className={s.countNote}>Nothing is written for them</span>
        </div>
      </div>

      {preview.ignored.length > 0 && <p className={pv.importNote}>Not read: {preview.ignored.join(', ')}.</p>}

      {held.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionHead}>Held back: correct these in the file and upload it again, or change the cycle by hand</div>
          <ul className={s.held} data-testid="weight-import-held">
            {held.slice(0, SHOWN).map((l) => (
              <li key={l.row} className={s.heldItem}>
                <span className={s.heldWhere}>Line {l.row}</span> · {[l.date, l.item_code].filter(Boolean).join(' · ') || '(empty)'}
                <ul className={s.heldReasons}>
                  <li>{l.reason}</li>
                </ul>
              </li>
            ))}
          </ul>
          {held.length > SHOWN && <p className={pv.importNote}>and {held.length - SHOWN} more</p>}
        </div>
      )}

      {updates.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionHead}>To be set</div>
          <div className={s.scroll}>
            <table className={s.table} data-testid="weight-import-updates">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Worker</th>
                  <th className={s.num}>Gross now</th>
                  <th className={s.num}>Becomes</th>
                </tr>
              </thead>
              <tbody>
                {updates.slice(0, SHOWN).map((l) => (
                  <tr key={l.row}>
                    <td>{l.row}</td>
                    <td>{l.date}</td>
                    <td>{l.item_code}</td>
                    <td>{l.worker_name || 'Unassigned'}</td>
                    <td className={s.num}>{kg(l.from_kg)}</td>
                    <td className={s.num}>{kg(l.gross_kg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {updates.length > SHOWN && <p className={pv.importNote}>and {updates.length - SHOWN} more</p>}
        </div>
      )}

      {unchanged.length > 0 && <p className={pv.importNote}>Already this weight, skipped: {unchanged.length === 1 ? 'line' : 'lines'} {unchanged.map((l) => l.row).join(', ')}.</p>}
    </>
  )
}
